import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useClient, useUpdateClient } from '../contexts/clients';
import { toPayload, validate, valuesFrom } from '../utils/form';
import ClientFields from './fields';

import '../styles/form.css';

// Two components on purpose.
//
// The outer one waits for the record; the inner one owns the form and seeds
// its state from what it receives, ONCE, in useState. There is no effect
// copying the record into state — that pattern re-renders twice and the
// template's lint rejects it. If the id changes, the `key` remounts the form
// and the seeding happens again, which is exactly the wanted behaviour.

export default function EditClientForm({ clientId }) {
    const { data: client, isLoading, error } = useClient(clientId);

    if (isLoading) {
        return <p className="client-form__loading">Cargando cliente...</p>;
    }

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar el cliente'}
            </p>
        );
    }

    return <ClientEditor key={client.id} client={client} />;
}

function ClientEditor({ client }) {
    const updateClient = useUpdateClient(client.id);

    const [values, setValues] = useState(() => valuesFrom(client));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        // Clearing the field's error as it is typed: leaving it on screen while
        // the person fixes it reads as if the fix did not count.
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            await updateClient.mutateAsync(toPayload(values));
            toast.success('Cliente guardado');
        } catch (err) {
            // TSS vite/04 — the backend answers 400 with { error, fields }, and
            // `fields` is per input. Those go next to the input; anything else
            // is a toast.
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar el cliente');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{client.name}</h2>
                <p className="client-form__subtitle">
                    Cliente #{client.id}
                    {/* The detail DOES carry the whole breakdown, and it is built so
                        that a state with none says 0 instead of saying nothing —
                        otherwise nobody can tell "zero" from "not measured". */}
                    {client.projects_summary?.total != null && (
                        <>
                            {' · '}
                            {client.projects_summary.total} proyectos
                            {client.projects_summary.total > 0 && (
                                <>
                                    {' ('}
                                    {client.projects_summary.active} activos,{' '}
                                    {client.projects_summary.paused} pausados,{' '}
                                    {client.projects_summary.closed} cerrados
                                    {')'}
                                </>
                            )}
                        </>
                    )}
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del cliente</h3>
                        <p className="client-form__section-text">
                            Con quién se trabaja y cómo ubicarlo.
                        </p>
                    </div>

                    <ClientFields
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={updateClient.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/clients" className="client-form__cancel">Volver</Link>
                    <button
                        type="submit"
                        className="client-form__submit"
                        disabled={updateClient.isPending}
                    >
                        {updateClient.isPending ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>
        </section>
    );
}
